import { pick } from "lodash";
import { useSigma } from "react-sigma-v2";
import { FC, useEffect, useRef } from "react";
import forceAtlas2 from "graphology-layout-forceatlas2";
import FA2Supervisor from "graphology-layout-forceatlas2/worker";

import { DateState, FiltersState, WYMMO_TAG } from "../types";

export const LAYOUT_TIMEOUT = 3000;

const GraphLayoutController: FC<{
  filters: FiltersState;
  date: DateState;
  setIsRunning: (isRunning: boolean) => void;
}> = ({ filters, date, setIsRunning }) => {
  const forceRef = useRef<FA2Supervisor | null>(null);
  const sigma = useSigma();
  const graph = sigma.getGraph();

  useEffect(() => {
    const fa2Graph = graph.nullCopy();
    graph.forEachNode((node, attributes) => {
      if (!attributes.hidden)
        fa2Graph.addNode(node, {
          ...attributes,
          fixed: attributes.wymmoType === WYMMO_TAG || attributes.fixed,
        });
    });
    graph.forEachEdge((edge, attributes, source, target) => {
      if (
        !attributes.hidden &&
        fa2Graph.hasNode(source) &&
        fa2Graph.hasNode(target)
      )
        fa2Graph.addEdgeWithKey(edge, source, target, attributes);
    });

    const force = new FA2Supervisor(fa2Graph, {
      settings: forceAtlas2.inferSettings(fa2Graph),
    });
    force.start();
    setIsRunning(true);
    forceRef.current = force;

    const handler = () => {
      fa2Graph.forEachNode((node, attributes) => {
        graph.mergeNodeAttributes(node, pick(attributes, "x", "y"));
      });
    };
    fa2Graph.on("eachNodeAttributesUpdated", handler);

    const timeout = window.setTimeout(() => {
      if (force.isRunning()) {
        force.stop();
        setIsRunning(false);
      }
    }, LAYOUT_TIMEOUT);

    return () => {
      window.clearTimeout(timeout);
      force.stop();
      setIsRunning(false);
      force.kill();
      fa2Graph.off("eachNodeAttributesUpdated", handler);
    };
  }, [graph, filters, date]);

  return null;
};

export default GraphLayoutController;
